import { cn } from '@plato/ui/util/classes';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkText } from './link-text.tsx';
import type { TextProps } from './text.tsx';
import { Text } from './text.tsx';

type Props = Omit<TextProps & { as: 'p' }, 'as'> & {
    canOpen?: boolean;
};

export function CollapsibleText({ className, children, canOpen, ...props }: Props) {
    const { t } = useTranslation();
    const textRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [isShort, setIsShort] = useState<boolean>();

    useEffect(() => {
        const textElement = textRef.current;

        if (!textElement || open) {
            return;
        }

        if (
            textElement.offsetWidth >= textElement.scrollWidth &&
            textElement.offsetHeight >= textElement.scrollHeight - 4
        ) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
            setIsShort(true);
        }
    });

    return (
        <div className={cn('flex flex-col flex-nowrap gap-2 items-end', className)}>
            <div className="relative grow self-start">
                <Text
                    className={cn(``, {
                        'overflow-hidden text-ellipsis max-h-20': !open,
                    })}
                    ref={textRef}
                    {...props}
                    as="p"
                >
                    <span className="whitespace-pre-line">{children}</span>
                </Text>
                {!open && !isShort && (
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-linear-to-t from-white to-transparent pointer-events-none" />
                )}
            </div>
            {canOpen && !isShort && (
                <LinkText
                    className="shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen((open) => !open);
                    }}
                >
                    {open
                        ? t('show_less', 'Weniger anzeigen', { ns: 'common' })
                        : t('show_more', 'Mehr anzeigen', { ns: 'common' })}
                </LinkText>
            )}
        </div>
    );
}
